/**
 * PvEArena — pve-arena.
 *
 * Sibling test surface to BrawlArena. Same geometry (200×60 walled arena
 * with 3 cover pillars + ruler ticks + 4 corner spawns) but recolored to
 * a forest-green palette that reads "wild zone" instead of "fight zone".
 *
 * Mobs (not bots) populate it: spawns come from
 * `content/zones/pve_arena.toml` via the existing zone-config pipeline
 * (GameRoom.onCreate → entitySpawner.spawnZoneMobs(loadedLevel.id)).
 *
 * Rules declared via `metadata.profile = 'pve_default'` — the
 * authored PvE preset (PvP off, friendlyFire off, ignore bot behavior,
 * 3000ms respawn, 2500ms post-respawn invuln). No bot lobby spawn
 * (profile keeps lobbyCount: 0). For per-map deviations, use
 * `metadata.overrides`. Profile lives at
 * src/shared/config/map_profiles/pve_default.ts.
 *
 * URL: https://h2000-ptr.djmsqrvve.com/pve
 *
 * Tile layout (200×60 tiles = 6400×1920 px) — identical to BrawlArena.
 * Spawn points spread NW/NE/SW/SE. The high (NW/NE) corners use
 * `spawnSnapMaxFallPx` so the player drops in via gravity.
 */

import { PlatformerLevel } from '../../shared/CharacterTypes';
import { GridLevel, LayerType } from '../../shared/TileEditorTypes';

const MAP_ID = 'pve-arena';
const MAP_NAME = 'PvE Arena';
const VERSION = '1.0';

const T = 32;          // tile size (px)
const W = 200;         // tiles wide → 6400 px
const H = 60;          // tiles tall → 1920 px

const WALL_THICKNESS = 4;  // tiles
const FLOOR_TOP_ROW = 53;  // tiles — top of the floor (playable space ends here)
const FLOOR_DEPTH = 6;     // tiles down to the bottom wall

// 4 respawn points — same layout as BrawlArena. NW/NE high-aerial, SW/SE
// floor corners. Spread evenly across the 200-tile arena.
const SPAWN_TILES = [
  { name: 'NW', x: 20,  y: 10 },
  { name: 'NE', x: 180, y: 10 },
  { name: 'SW', x: 70,  y: 51 },
  { name: 'SE', x: 130, y: 51 },
];
const spawnPoints = SPAWN_TILES.map(s => ({ x: s.x * T, y: s.y * T }));

// ============================================================================
// FOREST PALETTE — distinct from BrawlArena's crimson "fight zone"
// ============================================================================
const COLOR_BG       = '#03140a';  // very dark forest — reads "wild zone"
const COLOR_FLOOR    = '#1a3a1f';  // mossy forest floor
const COLOR_WALLS    = '#0e2a14';  // darker green wall stone
const COLOR_PILLAR   = '#2a5a32';  // mid-green pillar
const COLOR_TICK     = '#ffffff';  // ruler tick — keep white for legibility

// ============================================================================
// RUNTIME PLATFORMS — walls + floor + cover pillars
// ============================================================================
const runtimePlatforms = [
  // Floor (full width)
  {
    id: 'floor',
    x: 0,
    y: FLOOR_TOP_ROW * T,
    width: W * T,
    height: FLOOR_DEPTH * T,
    type: 'normal' as const,
    color: COLOR_FLOOR,
  },
  // Ceiling (full width)
  {
    id: 'ceiling',
    x: 0,
    y: 0,
    width: W * T,
    height: WALL_THICKNESS * T,
    type: 'normal' as const,
    color: COLOR_WALLS,
  },
  // Left wall
  {
    id: 'wall-left',
    x: 0,
    y: 0,
    width: WALL_THICKNESS * T,
    height: H * T,
    type: 'normal' as const,
    color: COLOR_WALLS,
  },
  // Right wall
  {
    id: 'wall-right',
    x: (W - WALL_THICKNESS) * T,
    y: 0,
    width: WALL_THICKNESS * T,
    height: H * T,
    type: 'normal' as const,
    color: COLOR_WALLS,
  },
  // Cover pillars — type:'cover' = walk-through for players, blocks
  // projectiles + LOS. 3 pillars at 50-tile intervals.
  ...[50, 100, 150].map((tileX, i) => ({
    id: `pillar-${i + 1}`,
    x: (tileX - 1) * T,
    y: 45 * T,
    width: 2 * T,
    height: 8 * T,
    type: 'cover' as const,
    color: COLOR_PILLAR,
  })),
  // Ruler ticks — type:'decorative' tiles labelled with tile-x position.
  // Same scheme as BrawlArena so distance reads identically across both
  // test surfaces.
  ...[25, 50, 75, 100, 125, 150, 175].map((tileX) => ({
    id: `ruler-${tileX}`,
    x: tileX * T - 2,
    y: (FLOOR_TOP_ROW - 1) * T,
    width: 4,
    height: T,
    type: 'decorative' as const,
    color: COLOR_TICK,
    label: String(tileX),
  })),
];

// ============================================================================
// PARALLAX — moody forest backdrop, distinct from brawl crimson
// ============================================================================

// ============================================================================
// EXPORT
// ============================================================================
export const PvEArena: GridLevel & Partial<PlatformerLevel> = {
  id: MAP_ID,
  name: MAP_NAME,
  version: VERSION,

  widthTiles: W,
  heightTiles: H,
  width: W * T,
  height: H * T,
  tileSize: T,

  backgroundColor: COLOR_BG,
  gravity: 1000,

  spawnPoints,
  // No defaultSpawn — random-pick from spawnPoints on each respawn.


  layers: [
    { name: LayerType.GROUND,    visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.OBJECTS,   visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.ENTITIES,  visible: true, locked: false, opacity: 1.0, tiles: new Map(), entities: [] },
    { name: LayerType.TRIGGERS,  visible: true, locked: false, opacity: 1.0, tiles: new Map() },
  ],

  platforms: runtimePlatforms,
  entities: [],
  spawnRules: [],
  type: 'field',  // 'field' triggers respawn-via-getDefaultSpawnPosition (random pick)

  metadata: {
    author: 'DJ',
    description: 'PvE testing arena — recolored brawl-arena clone, mobs from content/zones/pve_arena.toml.',
    tags: ['pve', 'arena', 'testing'],
    // Lobby tools available so DJ can spawn extra mobs / NPCs while testing.
    lobbyTools: { enabled: true },
    // Same raycast-snap setup as BrawlArena — high corner spawns drop via
    // gravity instead of teleporting to the floor.
    spawnSnapMaxFallPx: 320,
    // Map rule profile — authored at src/shared/config/map_profiles/pve_default.ts.
    // Defines the canonical PvE template: PvP off, friendlyFire off, bots
    // ignore players, 3s respawn, 2.5s post-respawn invuln. Per-map
    // deviations go in `overrides` below (currently none).
    profile: 'pve_default',
  },
};

export { W, H, T, MAP_ID, MAP_NAME, SPAWN_TILES };
